import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { makeStyles } from '@material-ui/core';
import { Content, useToast } from '@redshelf/redshelf-ui';

import {
  AlertBanner,
  AriaLiveRegion,
  DialogOverlay,
  DocumentFraming,
  DocumentModals,
  Footer,
  NetworkStatusAlert,
  ScrubBar,
  SideNav,
  SkipToContentLink,
  StatusPage,
  TopBar,
} from 'components';
import {
  DOCUMENT_STATES,
  SCREEN_SIZE_CATEGORIES,
  STATUS_CODES,
} from 'constants';
import {
  AnalyticsModifiersContext,
  BookValuesContext,
  BookLocationDocumentValuesContext,
  BookLocationModifiersContext,
  BookLocationValuesContext,
  DocumentModifiersContext,
  DocumentValuesContext,
  MobileAppValuesContext,
  NetworkStatusValuesContext,
  OfflineModifiersContext,
  OfflineValuesContext,
  TextToSpeechModifiersContext,
  TextToSpeechValuesContext,
  UserAgentValuesContext,
  UserContentItemModifiersContext,
  UserContentItemValuesContext,
  UserContentValuesContext,
  UserSessionValuesContext,
  PrintModifiersContext,
  PrintValuesContext,
  ViewportValuesContext,
} from 'contexts';
import { useDimensions, useKeyboardShortcut } from 'hooks';
import { useTTSDialog } from 'components/DialogOverlay/hooks/useTTSDialog';
import { DocumentProvider } from 'providers';
import { getLocalSettingsProxy, locationLogic } from 'utilities';
import {
  OfflineUnavailableMessage,
  DownloadErrorDialog,
} from 'components/Offline';
import PrintErrorDialog from 'components/Print/PrintErrorDialog';
import { TTSControls } from 'components/TTS';
import { CookieConsentBanner } from 'modules/CookieConsent';

const useStyles = makeStyles((theme) => {
  return {
    noDocument: {
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#FFFFFF',
    },
    box: {
      display: 'flex',
      flexFlow: 'column',
      height: '100%',
    },
    content: {
      backgroundColor: theme.palette.background.dark,
      minHeight: '100%',
      maxHeight: '100%',
      height: '100%',
      overflow: 'hidden',
      paddingTop: theme.spacing(8), // height of topBar
      paddingBottom: ({ footerRef }) => {
        const totalFooterHeight = footerRef.current?.clientHeight;
        return `${totalFooterHeight}px`;
      },
      paddingLeft: '0',
      paddingRight: '0',
      borderLeft: '0px solid transparent',
      transition: 'padding-top 500ms ease',
      [theme.breakpoints.up('sm')]: {
        // these animation durations are intended to sync with the learning tools pop-in/pop-out effect
        transition: `padding-top 500ms ease,
          margin-left 225ms cubic-bezier(0, 0, 0.2, 1) 0ms,
          border-left-width 225ms cubic-bezier(0, 0, 0.2, 1) 0ms`,
        marginLeft: ({ areLearningToolsOpen }) => {
          if (areLearningToolsOpen) {
            return '71px';
          }

          return 0;
        },
        borderLeftWidth: ({ learningToolsMenuName, areLearningToolsOpen }) => {
          if (learningToolsMenuName && areLearningToolsOpen) {
            return '256px';
          }

          return 0;
        },
      },
      [theme.breakpoints.down('xs')]: {
        paddingTop: theme.spacing(6), // height of topBar + 10
      },
    },
    contentExpanded: {
      // hides the menus on mobile
      [theme.breakpoints.down('xs')]: {
        paddingTop: '0px',
        paddingBottom: '0px !important',
      },
    },
    hiddenTopBar: {
      // hides the menus on mobile during scroll
      [theme.breakpoints.down('xs')]: {
        top: '-80px !important',
      },
    },
    footer: {
      position: 'absolute',
      [theme.breakpoints.up('sm')]: {
        zIndex: '1299', // any more than this and we overlap the Toasts, Modals and Audio Controls
      },
      bottom: '0px',
      left: '0',
      right: '0',
      transition: 'bottom 500ms ease',
    },
    hiddenFooter: {
      // hides the menus on mobile
      [theme.breakpoints.down('xs')]: {
        bottom: '-50px', // enough to hide the scrub bar, but keep the TTS menu
      },
    },
  };
});

const ReaderLayout = () => {
  const { removeFrame } = useContext(DocumentModifiersContext);
  const { state: documentState, frame } = useContext(DocumentValuesContext);
  const { book } = useContext(BookValuesContext);
  const { isMobileAppBookDownloaded } = useContext(MobileAppValuesContext);

  const textToSpeechModifiers = useContext(TextToSpeechModifiersContext);
  const textToSpeechValues = useContext(TextToSpeechValuesContext);
  const {
    voices,
    isSpeaking,
    isPaused,
    hasPrevious,
    hasNext,
    isTTSReady,
    ttsZoomPreference,
  } = textToSpeechValues;
  const {
    goToNext,
    goToPrevious,
    read,
    readFromCfi,
    pauseReading,
    stopReading,
    setTTSZoomPreference,
  } = textToSpeechModifiers;

  const { goToLocation } = useContext(BookLocationModifiersContext);
  const { currentBookLocation, goToLocationCalledAt } = useContext(
    BookLocationValuesContext,
  );
  const { currentBookLocationDocument } = useContext(
    BookLocationDocumentValuesContext,
  );

  const { isOnline, isOffline } = useContext(NetworkStatusValuesContext);
  const userContent = useContext(UserContentValuesContext);
  const { backToShelfInfo, isSessionExpired } = useContext(
    UserSessionValuesContext,
  );
  const { backToLabel, backToUrl } = backToShelfInfo;

  const {
    currentDocumentHighlights,
    currentDocumentNotes,
    currentDocumentCopiedTexts,
    selectedHighlight,
    selectedNote,
    ariaLiveMessageForUserContent,
  } = useContext(UserContentItemValuesContext);

  const userContentItemModifiers = useContext(UserContentItemModifiersContext);
  const {
    clearAllUserContent,
    setSelectedHighlightFromSelection,
    setSelectedHighlight,
    onNoteHighlightClick,
    setNoteToEdit,
    setSelectedCopiedText,
  } = userContentItemModifiers;

  const { downloadInterruptionError, clearAddMarkerToOfflineError } =
    useContext(OfflineModifiersContext);
  const { documentsDownloaded } = useContext(OfflineValuesContext);

  const analytics = useContext(AnalyticsModifiersContext);
  const { hasPrintError } = useContext(PrintValuesContext);
  const { setHasPrintError, retryPrintAction, closeErrorDialog } = useContext(
    PrintModifiersContext,
  );

  const [announceLocationChangeMessage, setAnnounceLocationChangeMessage] =
    useState(null);

  const { viewport } = useContext(ViewportValuesContext);
  const isSmallerThanPhoneWidth =
    viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.PHONE;
  const isSmallerThanTabletWidth =
    viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const [isReadyToPreloadNextDocument, setIsReadyToPreloadNextDocument] =
    useState(false);

  // SideNav state
  const [areLearningToolsOpen, setAreLearningToolsOpen] = useState();
  const [areLearningToolsOpenDesktop, setAreLearningToolsOpenDesktop] =
    useState(true);
  const [
    areLearningToolsOpenPhoneAndTablet,
    setAreLearningToolsOpenPhoneAndTablet,
  ] = useState(false);
  const [learningToolsMenuName, setLearningToolsMenuName] = useState(null);
  const [shouldFocusLearningTools, setShouldFocusLearningTools] =
    useState(false);
  const openDrawerButtonRef = useRef();
  const notesMenuOptionRef = useRef();

  const toggleLearningTools = useCallback(() => {
    setAreLearningToolsOpenDesktop(!areLearningToolsOpen);
    setAreLearningToolsOpenPhoneAndTablet(!areLearningToolsOpen);

    if (!isSmallerThanPhoneWidth) {
      setShouldFocusLearningTools(!areLearningToolsOpen);
    }
  }, [areLearningToolsOpen, isSmallerThanPhoneWidth]);

  useEffect(
    function determineIfLearningToolsAreOpen() {
      setAreLearningToolsOpen(
        isSmallerThanTabletWidth
          ? areLearningToolsOpenPhoneAndTablet
          : areLearningToolsOpenDesktop,
      );
    },
    [
      areLearningToolsOpenDesktop,
      areLearningToolsOpenPhoneAndTablet,
      isSmallerThanTabletWidth,
    ],
  );

  // set focus on the first list item in Learning Tools menu drawer when menu is opened from the TopBar
  useEffect(() => {
    if (shouldFocusLearningTools) {
      const activeElement = document.activeElement;

      if (activeElement === openDrawerButtonRef.current) {
        notesMenuOptionRef.current.firstChild.focus();
      }
    }
  }, [shouldFocusLearningTools]);

  const closeLearningTools = useCallback(() => {
    setAreLearningToolsOpenDesktop(false);
    setAreLearningToolsOpenPhoneAndTablet(false);
  }, []);

  useEffect(
    function closeMobileLearningToolsOnLocationChange() {
      if (isSmallerThanPhoneWidth) {
        closeLearningTools();
      }
    },
    [closeLearningTools, goToLocationCalledAt, isSmallerThanPhoneWidth],
  );

  const footerRef = useRef(null); // Footer ref
  const topBarRef = useRef(null);

  const [isThisFirstRender, setIsThisFirstRender] = useState(true);

  const [localSettings] = useState(getLocalSettingsProxy(book?.pk));

  useEffect(() => {
    if (isThisFirstRender) {
      // keeps track of new users to the reader
      // currently not used
      if (localSettings.newV7Counter > 0) {
        const currentValue = localSettings.newV7Counter;
        localSettings.newV7Counter = currentValue - 1;
      }

      if (backToLabel) {
        localSettings.backToLabel = backToLabel;
      }
      if (backToUrl) {
        localSettings.backToUrl = backToUrl;
      }
    }

    setIsThisFirstRender(false);
    // this should only execute once
  }, []);

  // TTSControls state
  const [TTSVisible, setTTSVisible] = useState(false);
  const ttsButtonRef = useRef();
  const onTTSClick = useCallback(() => {
    if (TTSVisible) {
      stopReading();
    }
    setTTSVisible(!TTSVisible);
  }, [TTSVisible, stopReading]);

  const {
    setIsTTSDialogOpen,
    shouldShowTTSDialog,
    startTTSAfterFitToScreen,
    setReadFrom,
    handleStartTTS,
  } = useTTSDialog();

  const onReadFromHere = () => {
    analytics.TTSStarted({
      TTSLocation: 'read from here',
    });

    if (!TTSVisible) {
      setTTSVisible(true);
    }

    if (isTTSReady) {
      if (shouldShowTTSDialog) {
        setIsTTSDialogOpen(true);

        setReadFrom(selectedHighlight);
        // in case TTS is on
        stopReading();
      } else {
        handleStartTTS(() => readFromCfi(selectedHighlight), selectedHighlight);
      }
    }

    clearAllUserContent();
  };

  const didFinishScrubbarLocationChange = useRef(true);
  const didTouchDocumentAfterLastLocationChange = useRef(false);
  const contentElementRef = useRef();

  const [isMobileScrollingView, setIsMobileScrollingView] = useState(false);

  const onActiveDocumentScroll = useCallback(() => {
    if (
      !didFinishScrubbarLocationChange.current ||
      !didTouchDocumentAfterLastLocationChange.current
    ) {
      return false;
    }

    setIsMobileScrollingView(true);
  }, []);

  const onActiveDocumentTouchStart = (event) => {
    didTouchDocumentAfterLastLocationChange.current = true;
    if (!userContent) {
      event.preventDefault();
      return false;
    }
  };

  const { userAgent } = useContext(UserAgentValuesContext);
  const onActiveDocumentTouchEnd = useCallback(
    (event) => {
      if (userContent) {
        userContentItemModifiers.onTouchEnd(event);
      } else {
        event.preventDefault();
        return false;
      }
    },
    [userContent, userContentItemModifiers],
  );

  const onActiveDocumentClick = useCallback(
    (event) => {
      if (userAgent.isChrome && userAgent.isAndroid) {
        event.preventDefault();
      }
      setIsMobileScrollingView(false);
    },
    [userAgent],
  );

  const onActiveDocumentMouseDown = useCallback(
    (event) => {
      if (!userContent) {
        event.preventDefault();
        return false;
      }
    },
    [userContent],
  );

  const onActiveDocumentMouseUp = useCallback(
    (event) => {
      if (userContent) {
        userContentItemModifiers.onMouseUp(event);
      } else {
        event.preventDefault();
        return false;
      }
    },
    [userContent, userContentItemModifiers],
  );

  const onActiveDocumentSelectionChange = useCallback(
    (event) => {
      if (userContent) {
        userContentItemModifiers.onSelectionChange(event);
      } else {
        event.preventDefault();
        return false;
      }
    },
    [userContent, userContentItemModifiers],
  );

  // prevent Context Menu
  const onContextMenu = useCallback((event) => {
    event.preventDefault();
    return false;
  }, []);

  const onActiveDocumentCopy = useCallback((event) => {
    event.preventDefault();
    return false;

    // TODO: Copy does not trigger once the menu is open, and will need to be rethought a bit.
    // if (drmValues.copyEnabled) {
    //   openCopyMenu(event);
    // }
  }, []);

  const onActiveDocumentLinkClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const href =
        event.currentTarget.parentElement?.getAttribute('data-link-href') ??
        event.currentTarget.getAttribute('href');

      const newLocation = locationLogic.getLocationForInternalLink(
        currentBookLocationDocument,
        href,
      );

      if (newLocation) {
        goToLocation(newLocation);
      }
    },
    [goToLocation, currentBookLocationDocument],
  );

  const [bookLocationForModalCoordinates, setBookLocationForModalCoordinates] =
    useState(null);
  const onActiveDocumentCopiedTextClick = useCallback(
    (copiedText) => {
      setSelectedCopiedText(copiedText);
      setBookLocationForModalCoordinates(copiedText.bookLocation);
    },
    [setSelectedCopiedText, setBookLocationForModalCoordinates],
  );

  // TTS responsive accordion state
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const handleAccordionClick = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  useEffect(() => {
    // Once the document and all subresources have loaded for the active document, preload the next document
    setIsReadyToPreloadNextDocument(
      documentState >= DOCUMENT_STATES.LOADED && isOnline,
    );
  }, [documentState, isOnline]);

  const onChooseNewLocation = useCallback(
    async (newLocation, callback = () => {}) => {
      if (newLocation) {
        didFinishScrubbarLocationChange.current = false;
        didTouchDocumentAfterLastLocationChange.current = false;

        await goToLocation(newLocation);

        callback();

        didFinishScrubbarLocationChange.current = true;
      }
    },
    [goToLocation],
  );

  useKeyboardShortcut(
    ['ctrl+alt+h', 'ctrl+command+h'],
    setSelectedHighlightFromSelection,
    {
      event: 'keydown',
    },
  );

  const preventDefault = (event) => {
    event.preventDefault();
    return false;
  };

  const alertBannerRef = useRef(null);
  const { dimensions: alertBannerDimensions } = useDimensions(
    alertBannerRef.current,
    75,
  );
  const alertBannerHeight = alertBannerDimensions
    ? alertBannerDimensions[1]
    : 0;

  const classes = useStyles({
    areLearningToolsOpen,
    learningToolsMenuName,
    footerRef,
  });

  const focusFrame = useCallback(() => {
    clearAllUserContent(); // clear all selections, unsaved highlights, notes and flashcards, citation modal
    setNoteToEdit(null); // clear edit Note Modal

    // close all popover menu and dialog boxes
    const elementsToClose = document.querySelectorAll(
      '.MuiPopover-paper, .MuiDialog-root',
    );

    if (elementsToClose?.length) {
      elementsToClose.forEach((element) => {
        const btnElement = element.querySelector('button[aria-label="Close"]');
        const backDrop = element.querySelector('.MuiBackdrop-root');
        if (backDrop) {
          backDrop.click(); // backdrop Click for dialog boxes
        } else if (btnElement) {
          btnElement.click(); //1st btn in menu is close btn
        } else {
          element.parentElement?.children[0].click(); // for menu without close btn
        }
      });
    }

    // making sure focus is in the iframe
    frame.click();
    frame.focus();
  }, [frame, clearAllUserContent, setNoteToEdit]);

  useKeyboardShortcut(['alt+ctrl+b', 'command+ctrl+b'], focusFrame, {
    event: 'keydown',
  });

  const [documentAvailable, setDocumentAvailable] = useState(isOnline);
  const [shouldShowOfflineUI, setShouldShowOfflineUI] = useState(false);
  const frameRemovalTimeoutRef = useRef(null);
  const previousOnlineState = useRef(isOnline);

  useEffect(() => {
    // Clear any pending frame removal when coming back online
    if (isOnline && !previousOnlineState.current) {
      if (frameRemovalTimeoutRef.current) {
        clearTimeout(frameRemovalTimeoutRef.current);
        frameRemovalTimeoutRef.current = null;
      }
      setDocumentAvailable(true);
      setShouldShowOfflineUI(false);
      previousOnlineState.current = isOnline;
      return;
    }

    // If going offline, check document availability
    if (!isOnline) {
      // When coming back online from offline mode, there is a brief moment where the book object is reconstructed.
      // During this time, the BookDocument objects in documentsDownloaded contain outdated instances so it will not include the current BookDocument.
      // This causes the document to become unavailable during this time, and also loses the user's scrolling position.
      // So to check downloaded status here, we need to check the document index instead of the document object.
      const documentIndicesDownloaded = Array.from(documentsDownloaded).map(
        (document) => document.index,
      );

      const isDocumentDownloaded =
        isMobileAppBookDownloaded ||
        documentIndicesDownloaded.includes(currentBookLocation?.documentIndex);

      if (isDocumentDownloaded) {
        // Document is available offline, keep frame
        setDocumentAvailable(true);
        setShouldShowOfflineUI(false);
      } else {
        // Document not available offline - show offline UI but delay frame removal
        setShouldShowOfflineUI(true);

        // Clear any existing timeout
        if (frameRemovalTimeoutRef.current) {
          clearTimeout(frameRemovalTimeoutRef.current);
        }

        // Only remove frame after a delay to avoid interrupting TTS during brief connectivity issues
        frameRemovalTimeoutRef.current = setTimeout(() => {
          setDocumentAvailable(false);
          frameRemovalTimeoutRef.current = null;
        }, 3000); // 3 second delay before frame removal
      }
    }

    previousOnlineState.current = isOnline;
  }, [
    isOnline,
    currentBookLocation?.documentIndex,
    documentsDownloaded,
    isMobileAppBookDownloaded,
  ]);

  useEffect(() => {
    // Only remove frame if document is truly unavailable after delay
    if (!documentAvailable && !shouldShowOfflineUI) {
      removeFrame();
    }
  }, [removeFrame, documentAvailable, shouldShowOfflineUI]);

  useEffect(() => {
    // Stop TTS if offline and speaking and document is truly unavailable
    if (isOffline && isSpeaking && shouldShowOfflineUI) {
      stopReading();
    }
  }, [isOffline, isSpeaking, shouldShowOfflineUI, stopReading]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (frameRemovalTimeoutRef.current) {
        clearTimeout(frameRemovalTimeoutRef.current);
      }
    };
  }, []);

  const noDocRef = useRef(null);

  const { closeToast } = useToast();

  useEffect(() => {
    // close open toasts if there is a downloadInterrputed coz of sudden network loss
    // or if sessionExpired with open toasts

    if ((!isOnline && downloadInterruptionError) || isSessionExpired) {
      closeToast();
    }
  }, [downloadInterruptionError, closeToast, isOnline, isSessionExpired]);

  const highlightToFocusRef = useRef(null);
  const resetHighlightToFocus = useCallback(() => {
    highlightToFocusRef.current = null;
  }, []);

  return !isSessionExpired ? (
    <Content
      className={classes.box}
      isRoot
      onCopy={preventDefault}
      onDragStart={preventDefault}
    >
      <SkipToContentLink />
      <CookieConsentBanner />
      <AlertBanner alertBannerRef={alertBannerRef} />
      <TopBar
        areLearningToolsOpen={areLearningToolsOpen}
        alertBannerHeight={alertBannerHeight}
        bookLocation={currentBookLocation}
        className={isMobileScrollingView ? classes.hiddenTopBar : ''}
        openDrawerButtonRef={openDrawerButtonRef}
        setAnnounceLocationChangeMessage={setAnnounceLocationChangeMessage}
        toggleNav={toggleLearningTools}
        topBarRef={topBarRef}
      />
      <SideNav
        alertBannerHeight={alertBannerHeight}
        currentMenuChanged={setLearningToolsMenuName}
        footerHeight={footerRef.current?.clientHeight ?? 0}
        closeLearningTools={closeLearningTools}
        isOpenOnDesktop={areLearningToolsOpenDesktop}
        isOpenOnPhoneAndTablet={areLearningToolsOpenPhoneAndTablet}
        notesMenuOptionRef={notesMenuOptionRef}
      />
      <NetworkStatusAlert
        {...{ areLearningToolsOpen, learningToolsMenuName }}
      />
      {documentAvailable && !shouldShowOfflineUI ? (
        <Content
          classes={{
            content: isMobileScrollingView
              ? `${classes.content} ${classes.contentExpanded}`
              : classes.content,
          }}
          ref={contentElementRef}
        >
          {[
            currentBookLocation,
            isReadyToPreloadNextDocument
              ? currentBookLocation.document.next?.bookLocation
              : null,
          ].map((bookLocation, n) => {
            const isActive = n === 0;
            return bookLocation ? (
              isActive ? (
                <DocumentFraming
                  id={'activeDocument'}
                  bookLocation={bookLocation}
                  highlightToFocus={highlightToFocusRef.current}
                  isActive={true}
                  key={bookLocation.document.index} // help react know when to reuse already rendered instances
                  onContextMenu={onContextMenu}
                  onCopiedTextClick={onActiveDocumentCopiedTextClick}
                  onDocumentClick={onActiveDocumentClick}
                  onDocumentCopy={onActiveDocumentCopy}
                  onDocumentLinkClick={onActiveDocumentLinkClick}
                  onDocumentMouseDown={onActiveDocumentMouseDown}
                  onDocumentMouseUp={onActiveDocumentMouseUp}
                  onDocumentScroll={onActiveDocumentScroll}
                  onDocumentSelectionChange={onActiveDocumentSelectionChange}
                  onDocumentTouchEnd={onActiveDocumentTouchEnd}
                  onDocumentTouchStart={onActiveDocumentTouchStart}
                  onHighlightClick={setSelectedHighlight}
                  onNoteHighlightClick={onNoteHighlightClick}
                  resetHighlightToFocus={resetHighlightToFocus}
                  setAnnounceLocationChangeMessage={
                    setAnnounceLocationChangeMessage
                  }
                  startTTSAfterFitToScreen={startTTSAfterFitToScreen}
                  userContentCopiedTexts={currentDocumentCopiedTexts}
                  userContentHighlights={currentDocumentHighlights}
                  userContentNotes={currentDocumentNotes}
                  userContentSelectedHighlight={selectedHighlight}
                  userContentSelectedNote={selectedNote}
                />
              ) : (
                // wrapping the non-active documents in DocumentProvider creates a throwaway context,
                // so they will not affect the active Document context
                <DocumentProvider key="inactive-document">
                  <DocumentFraming
                    id={null}
                    bookLocation={bookLocation}
                    isActive={false}
                    key={bookLocation.document.index}
                  />
                </DocumentProvider>
              )
            ) : null;
          })}
          <DocumentModals
            highlightToFocusRef={highlightToFocusRef}
            setBookLocationForModalCoordinates={
              setBookLocationForModalCoordinates
            }
            bookLocationForModalCoordinates={bookLocationForModalCoordinates}
            onReadFromHere={onReadFromHere}
          />
        </Content>
      ) : (
        <main
          tabindex="-1"
          className={classes.noDocument}
          ref={noDocRef}
          aria-label="You're Offline. To access this page, go back online or, if available, download this chapter for offline use."
        >
          <OfflineUnavailableMessage
            offlineAvailable={true}
            isPageUnavailable={true}
          />
        </main>
      )}

      {/* Download interrupted by network loss */}
      {downloadInterruptionError && (
        <DownloadErrorDialog
          open={true}
          closeAction={() => {
            clearAddMarkerToOfflineError();
          }}
        />
      )}
      {hasPrintError && (
        <PrintErrorDialog
          open={true}
          closeAction={() => closeErrorDialog()}
          buttonAction={() => {
            retryPrintAction();
            setHasPrintError(false);
          }}
        />
      )}
      <Footer
        id="scrub-bar"
        footerRef={footerRef}
        className={
          isMobileScrollingView
            ? `${classes.footer} ${classes.hiddenFooter}`
            : classes.footer
        }
      >
        <TTSControls
          visible={TTSVisible}
          currentVolume={textToSpeechValues.volume}
          currentVoice={textToSpeechValues.voice}
          currentRate={textToSpeechValues.rate}
          voices={voices}
          onPlay={read}
          onPause={pauseReading}
          isPaused={isPaused}
          isPlaying={isSpeaking}
          onRewind={goToPrevious}
          onForward={goToNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          canRead={isTTSReady && currentBookLocation.document.characterCount}
          onVoiceChange={textToSpeechModifiers.setVoice}
          onRateChange={textToSpeechModifiers.setRate}
          onVolumeChange={textToSpeechModifiers.setVolume}
          ttsZoomPreference={ttsZoomPreference}
          onTTSZoomPreferenceChange={setTTSZoomPreference}
          onClose={() => {
            stopReading();
            // send focus back to TTS button
            ttsButtonRef.current.focus();
            setTTSVisible(false);
            setIsAccordionOpen(false);
          }}
          isAccordionOpen={isAccordionOpen}
          handleAccordionClick={() => handleAccordionClick()}
          setIsTTSDialogOpen={setIsTTSDialogOpen}
          shouldShowTTSDialog={shouldShowTTSDialog}
          handleStartTTS={handleStartTTS}
        />
        <ScrubBar
          isMobileScrollingView={isMobileScrollingView}
          onChooseNewLocation={onChooseNewLocation}
          onTTSClick={onTTSClick}
          ttsActive={TTSVisible}
          ttsButtonRef={ttsButtonRef}
        />
      </Footer>
      <AriaLiveRegion
        id="annotation-save-delete"
        isPolite={false}
        message={ariaLiveMessageForUserContent}
      />
      <AriaLiveRegion
        id="location-change"
        message={announceLocationChangeMessage}
      />
      <DialogOverlay />
    </Content>
  ) : (
    <StatusPage
      statusCode={STATUS_CODES.FORBIDDEN}
      bookPk={currentBookLocation.book.pk}
    />
  );
};

export default ReaderLayout;
